import type { z } from "zod";

/**
 * A route contract: one HTTP endpoint described by zod schemas. The API validates
 * params/query/body against it and types its handler from it; clients get a typed
 * method per route. This is the ts-rest idea without the dependency (ts-rest still
 * pins zod 3 and Nest ≤ 11 — PLAN Phase 2 spike, 2026-10-01).
 */
export type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface RouteDef {
  readonly method: Method;
  /** Express-style path, e.g. `/patients/:patientId`. Always absolute, no trailing slash. */
  readonly path: string;
  readonly summary: string;
  readonly params?: z.ZodType;
  readonly query?: z.ZodType;
  readonly body?: z.ZodType;
  /** Status → response body schema. 204 may map to `z.undefined()`. */
  readonly responses: Readonly<Record<number, z.ZodType>>;
}

export type Routes = Readonly<Record<string, RouteDef>>;

/** Identity with inference: keeps literal method/path/schemas for the type helpers below. */
export function defineRoutes<const T extends Routes>(routes: T): T {
  return routes;
}

type Out<S> = S extends z.ZodType ? z.output<S> : undefined;
type In<S> = S extends z.ZodType ? z.input<S> : undefined;

/** What a handler receives after validation (zod *output*: coerced, defaulted). */
export interface RouteInput<R extends RouteDef> {
  readonly params: Out<R["params"]>;
  readonly query: Out<R["query"]>;
  readonly body: Out<R["body"]>;
}

/** What a client sends (zod *input*: before coercion/defaults). */
export type ClientInput<R extends RouteDef> = (R["params"] extends z.ZodType
  ? { params: In<R["params"]> }
  : { params?: undefined }) &
  (R["query"] extends z.ZodType ? { query?: In<R["query"]> } : { query?: undefined }) &
  (R["body"] extends z.ZodType ? { body: In<R["body"]> } : { body?: undefined });

/** Union of `{ status, body }` over the declared responses. */
export type RouteResponse<R extends RouteDef> = {
  [S in keyof R["responses"] & number]: { status: S; body: z.output<R["responses"][S]> };
}[keyof R["responses"] & number];

/** The success body (the lowest 2xx status declared). */
export type SuccessBody<R extends RouteDef> = Extract<
  RouteResponse<R>,
  { status: 200 | 201 | 204 }
>["body"];
